import type { Entity } from '@backstage/catalog-model';
import type { KnownKinds } from '@ipen-portal/plugin-common';
import type { ComponentProps } from 'react';

import { useGetEntityByRef } from '@ipen-portal/plugin-common-react';

import { KeyRoleListItems } from './KeyRoleListItems';

type Props<E extends Entity> = {
  /**
   * A predicate that determines if the entity is of the type that this extractor can handle.
   */
  entityPredicate: (entity: Entity) => entity is E;
  /**
   * Used as fallback in case the relational field is not a full qualified entityRef.
   */
  fieldKind?: KnownKinds;
  referredEntityPredicate?: (entity: Entity) => entity is Entity;
  /**
   * An extractor function extracting the relevant relation field where the key roles should be extracted from.
   */
  relationFieldExtractor: (entity: E) => string | undefined;
} & ComponentProps<typeof KeyRoleListItems>;

export const RelationalKeyRoleListItems = <E extends Entity>({
  entity,
  entityPredicate,
  fieldKind: fallbackKind,
  referredEntityPredicate,
  relationFieldExtractor,
  ...rest
}: Props<E>) => {
  const ref = entityPredicate(entity) ? relationFieldExtractor(entity) : null;
  const { value: referredEntity } = useGetEntityByRef(ref, { fallbackKind, predicate: referredEntityPredicate });

  if (!referredEntity) {
    return null;
  }
  return <KeyRoleListItems {...rest} entity={referredEntity} />;
};
